import { DISPATCH_TICKET_STATUSES } from 'utils/constants'
import { Stack, Typography } from '@mui/material_modern'
import React from 'react'
import { dateFormatter, timeFormatter } from 'utils/data-manipulation/valueFormatter'

/**
 * Splices item from startIndex in sourceList and moves it to endIndex
 * @param {Object[]} sourceList - Current array of items in source
 * @param {number} startIndex - Previous index of moved item
 * @param {number} endIndex - New index of moved item
 */
export const reorderItems = ({ sourceList, startIndex, endIndex }) => {
  const sourceListCopy = Array.from(sourceList)
  const ticketsToPatch = []

  const [removed] = sourceListCopy.splice(startIndex, 1)
  sourceListCopy.splice(endIndex, 0, removed)

  const updatedSourceList = sourceListCopy.map((ticket, index) => {
    if (ticket.loadBoardIndex !== index) {
      ticketsToPatch.push({ id: ticket.id, loadBoardIndex: index, hash: ticket.hash })
    }
    return {
      ...ticket,
      loadBoardIndex: index,
    }
  })

  return {
    updatedSourceList,
    ticketsToPatch,
  }
}

/**
 * Splices item from sourceList and adds it to destinationList
 * @param {Object[]} sourceList - Current array of items where item was picked up
 * @param {Object[]} destinationList - Current array of items where item was dropped off
 * @param {Object} sourceInfo - Object containing information about the source where the item was picked up
 * @param {string} sourceInfo.droppableId - ID of droppable area where item was picked up
 * @param {number} sourceInfo.index - Previous index of moved item in source
 * @param {Object} destinationInfo - Object containing information about the destination where the item was dropped off
 * @param {string} destinationInfo.droppableId - ID of droppable area where item was dropped off
 * @param {number} destinationInfo.index - New index of moved item in destination
 * @param {Date} selectedDate - The date of the load board page currently being viewed
 */
export const moveItem = ({ sourceList, destinationList, sourceInfo, destinationInfo, selectedDate }) => {
  const sourceListCopy = Array.from(sourceList)
  const destinationListCopy = Array.from(destinationList)
  const ticketsToPatch = []

  const [removed] = sourceListCopy.splice(sourceInfo.index, 1)
  const removedCopy = { ...removed } // create mutable copy of removed

  // Update schedule date when moving from either past or requested columns
  if (
    (sourceInfo.droppableId.endsWith('past') || sourceInfo.droppableId === 'requested') &&
    !destinationInfo.droppableId.endsWith('past')
  ) {
    removedCopy.scheduleDate = selectedDate.toISOString()
  }

  if (destinationInfo.droppableId.startsWith('unassigned')) {
    removedCopy.driverId = null
    if (
      removedCopy.status === DISPATCH_TICKET_STATUSES.ASSIGNED.id ||
      removedCopy.status === DISPATCH_TICKET_STATUSES.REQUESTED.id
    ) {
      removedCopy.status = DISPATCH_TICKET_STATUSES.UNASSIGNED.id
    }
  } else {
    removedCopy.driverId = destinationInfo.droppableId.endsWith('past')
      ? destinationInfo.droppableId.slice(0, -4)
      : destinationInfo.droppableId
    if (
      removedCopy.status === DISPATCH_TICKET_STATUSES.UNASSIGNED.id ||
      removedCopy.status === DISPATCH_TICKET_STATUSES.REQUESTED.id
    ) {
      removedCopy.status = DISPATCH_TICKET_STATUSES.ASSIGNED.id
    }
  }

  destinationListCopy.splice(destinationInfo.index, 0, removedCopy)

  const updatedSourceList = sourceListCopy.map((ticket, index) => {
    if (ticket.loadBoardIndex !== index) {
      ticketsToPatch.push({ id: ticket.id, loadBoardIndex: index, hash: ticket.hash })
    }
    return {
      ...ticket,
      loadBoardIndex: index,
    }
  })

  const updatedDestinationList = destinationListCopy.map((ticket, index) => {
    if (ticket.id === removedCopy.id) {
      ticketsToPatch.push({
        id: ticket.id,
        loadBoardIndex: index,
        driverId: ticket.driverId,
        status: ticket.status,
        scheduleDate: removedCopy.scheduleDate,
        hash: ticket.hash,
      })
    } else if (ticket.loadBoardIndex !== index) {
      ticketsToPatch.push({ id: ticket.id, loadBoardIndex: index, hash: ticket.hash })
    }
    return {
      ...ticket,
      loadBoardIndex: index,
    }
  })

  return {
    updatedSourceList,
    updatedDestinationList,
    ticketsToPatch,
  }
}

export const combineDateAndTime = (date, time) => {
  const datePart = date.toLocaleString('en-US').split(',')[0]
  const timePart = time.toLocaleString('en-US').split(',')[1]

  const newDateTime = new Date(`${datePart} ${timePart}`)
  return newDateTime.toISOString()
}

export function getContainerFields() {
  return [
    [
      {
        name: 'assetName',
        label: 'Container Name',
        dataTest: 'container-name',
        fieldType: 'static',
      },
      {
        name: 'type',
        label: 'Container Type',
        dataTest: 'container-type',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'statusName',
        label: 'Status',
        dataTest: 'container-status',
        fieldType: 'static',
      },
      {
        name: 'location',
        label: 'Location',
        dataTest: 'container-location',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'daysOnSite',
        label: 'Days On Site',
        dataTest: 'container-days-on-site',
        fieldType: 'static',
      },
      {
        name: 'notes',
        label: 'Notes',
        dataTest: 'container-notes',
        fieldType: 'static',
      },
    ],
  ]
}

export function getTruckFields() {
  return [
    [
      {
        name: 'assetName',
        label: 'Truck Name',
        dataTest: 'truck-name',
        fieldType: 'static',
      },
      {
        name: 'type',
        label: 'Truck Type',
        dataTest: 'truck-type',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'make',
        label: 'Make',
        dataTest: 'truck-make',
        fieldType: 'static',
      },
      {
        name: 'model',
        label: 'Model',
        dataTest: 'truck-model',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'licensePlate',
        label: 'License Plate',
        dataTest: 'trailer-license-plate',
        fieldType: 'static',
      },
      {
        name: 'plateState',
        label: 'Plate State',
        dataTest: 'trailer-plate-state',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'statusName',
        label: 'Status',
        dataTest: 'truck-status',
        fieldType: 'static',
      },
      {
        name: 'location',
        label: 'Location',
        dataTest: 'truck-location',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'daysOnSite',
        label: 'Days On Site',
        dataTest: 'truck-days-on-site',
        fieldType: 'static',
      },
      {
        name: 'notes',
        label: 'Notes',
        dataTest: 'truck-notes',
        fieldType: 'static',
      },
    ],
  ]
}

export function getTrailerFields() {
  return [
    [
      {
        name: 'assetName',
        label: 'Trailer Name',
        dataTest: 'trailer-name',
        fieldType: 'static',
      },
      {
        name: 'type',
        label: 'Trailer Type',
        dataTest: 'trailer-type',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'licensePlate',
        label: 'License Plate',
        dataTest: 'trailer-license-plate',
        fieldType: 'static',
      },
      {
        name: 'plateState',
        label: 'Plate State',
        dataTest: 'trailer-plate-state',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'statusName',
        label: 'Status',
        dataTest: 'trailer-status',
        fieldType: 'static',
      },
      {
        name: 'location',
        label: 'Location',
        dataTest: 'trailer-location',
        fieldType: 'static',
      },
    ],
    [
      {
        name: 'daysOnSite',
        label: 'Days On Site',
        dataTest: 'trailer-days-on-site',
        fieldType: 'static',
      },
      {
        name: 'notes',
        label: 'Notes',
        dataTest: 'trailer-notes',
        fieldType: 'static',
      },
    ],
  ]
}

/**
 * creates a formatted option element for selecting a trailer
 * @param trailer
 * @return {{name: JSX.Element, id: string}}
 */
export const createTrailerOption = trailer => {
  return {
    id: trailer?.id,
    name: (
      <Stack>
        <Typography variant="body2">{trailer?.assetName}</Typography>
        <Typography variant="caption">{trailer?.type} </Typography>
      </Stack>
    ),
  }
}

/**
 * creates a formatted header text row for distinguishing between different sections in the dropdown
 * @param subHeadingText
 * @return {{isHeader: boolean, name: JSX.Element}}
 */
export const getSubHeadingSelectRow = subHeadingText => {
  return {
    name: (
      <Typography variant="h6" color="textSecondary">
        {subHeadingText}
      </Typography>
    ),
    isHeader: true,
  }
}

export const getDayHourSecondString = (startDateTime, completionDateTime) => {
  const diff = new Date(completionDateTime).getTime() - new Date(startDateTime).getTime()

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (days > 0) {
    return `${days} days, ${hours} hours, ${minutes} minutes`
  } else if (hours > 0) {
    return `${hours} hours, ${minutes} minutes`
  } else {
    return `${minutes} minutes`
  }
}
